// src/modules/requerimiento/components/ReporteComponents.jsx
// Piezas de la lista de verificación de Revisión en Sistemas (adición y mantenimiento): una línea
// ok / falta / advertencia, la línea "¿reporta?" de un equipo y el diálogo con sus plataformas.
import React from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SensorsIcon from "@mui/icons-material/Sensors";
import CloseIcon from "@mui/icons-material/Close";
import { formatearFechaReporte, haceDias, DIAS_REPORTE_VIEJO } from "../utils/reporteModem";

const AZUL_EQUIPOS = "#0284C7";
const VERDE = "#16A34A";
const AMBAR = "#F59E0B";

/** Una línea de la lista: check verde / x roja (o alerta ámbar si es advertencia). */
export function ItemVerificacion({ ok, textoOk = "", textoFalta, advertencia = false, detalle, onClick }) {
  const color = ok ? VERDE : advertencia ? AMBAR : "error.main";
  const Icono = ok ? CheckCircleIcon : advertencia ? ErrorOutlineIcon : CancelIcon;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      onMouseDown={onClick ? (e) => e.stopPropagation() : undefined}
      sx={onClick ? { cursor: "pointer", borderRadius: "8px", mx: -0.5, px: 0.5, "&:hover": { bgcolor: "action.hover" } } : undefined}
    >
      <Icono sx={{ color, fontSize: 20 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: ok ? "text.primary" : color }}>
          {ok ? textoOk : textoFalta}
        </Typography>
        {detalle && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{detalle}</Typography>}
      </Box>
    </Stack>
  );
}

/** La línea "reporta / no reporta / verificando" de un equipo; clic muestra las plataformas. */
export function LineaReporte({ reporte, onClick }) {
  if (!reporte || reporte === "verificando") {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <CircularProgress size={18} />
        <Typography variant="body2">Verificando si reporta…</Typography>
      </Stack>
    );
  }
  if (reporte.estado === "reporta") {
    const nombres = reporte.plataformas.map((p) => p.titulo).join(", ");
    const cuando = [formatearFechaReporte(reporte.ultimoMs), reporte.dias != null ? `(${haceDias(reporte.dias)})` : ""]
      .filter(Boolean).join(" ");
    return (
      <ItemVerificacion
        ok={!reporte.desactualizado}
        textoOk={`Reporta en ${nombres}`}
        textoFalta={`En ${nombres}, pero no reporta ${haceDias(reporte.dias)}`}
        detalle={cuando ? `Último reporte: ${cuando}` : undefined}
        advertencia
        onClick={onClick}
      />
    );
  }
  if (reporte.estado === "no_reporta") {
    return (
      <ItemVerificacion
        ok={false}
        textoFalta="No reporta en ninguna plataforma"
        detalle="Revisa la instalación o la configuración en la plataforma."
        onClick={onClick}
      />
    );
  }
  return (
    <ItemVerificacion
      ok={false}
      textoFalta="No se pudo verificar"
      detalle="Haz clic para intentar de nuevo."
      advertencia
      onClick={onClick}
    />
  );
}

/** "Verificar si reporta": en qué plataformas está el equipo y su último reporte en cada una. */
export function PlataformasDialog({ modem, reporte, onReintentar, onVerEquipo, onClose }) {
  const verificando = !reporte || reporte === "verificando";
  const plataformas = !verificando ? reporte.plataformas || [] : [];
  return (
    <Dialog open={!!modem} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: "12px" } }}>
      {modem && (
        <>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "8px", bgcolor: "rgba(2,132,199,0.14)", color: AZUL_EQUIPOS, display: "grid", placeItems: "center", flexShrink: 0 }}>
              <SensorsIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>Verificar si reporta</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {modem.emai || modem.num_sim}{modem.placa ? ` · ${modem.placa}` : ""}
              </Typography>
            </Box>
            <IconButton size="small" aria-label="Cerrar" onClick={onClose}><CloseIcon /></IconButton>
          </Stack>
          <DialogContent dividers sx={{ px: 2.5 }}>
            {verificando ? (
              <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
                <CircularProgress size={26} />
                <Typography variant="body2" color="text.secondary">Consultando plataformas…</Typography>
              </Stack>
            ) : reporte.estado === "no_se_pudo" ? (
              <Typography variant="body2" color="text.secondary">No se pudieron consultar las plataformas.</Typography>
            ) : plataformas.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                El equipo no aparece en ninguna plataforma (Flespi, Wialon, LeaseControl, TRS Rastreo ni YPFB).
              </Typography>
            ) : (
              <Stack spacing={1}>
                {plataformas.map((p) => {
                  const dias = p.ultimoMs ? Math.max(0, Math.floor((Date.now() - p.ultimoMs) / 86400000)) : null;
                  const viejo = dias == null || dias >= DIAS_REPORTE_VIEJO;
                  return (
                    <Stack
                      key={p.titulo}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ p: 1.25, borderRadius: "10px", border: "1px solid", borderColor: "divider" }}
                    >
                      {viejo
                        ? <ErrorOutlineIcon sx={{ color: AMBAR, fontSize: 20 }} />
                        : <CheckCircleIcon sx={{ color: VERDE, fontSize: 20 }} />}
                      <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>{p.titulo}</Typography>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="body2">{p.ultimoMs ? formatearFechaReporte(p.ultimoMs) : "Sin fecha"}</Typography>
                        {dias != null && <Typography variant="caption" color="text.secondary">{haceDias(dias)}</Typography>}
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            <Button onClick={onVerEquipo} sx={{ textTransform: "none", mr: "auto" }}>Ver ficha del equipo</Button>
            <Button
              onClick={onReintentar}
              disabled={verificando}
              startIcon={<RefreshIcon />}
              variant="contained"
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Verificar de nuevo
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
